/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import {downloadFile, nullify, ResponseData, stopPropagation} from '@dvrd/dvr-controls';
import React, {useContext, useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router';
import Account from '../../../models/accountModel';
import BolInvoiceRequest from '../../../models/bolInvoiceRequestModel';
import Invoice from '../../../models/invoiceModel';
import {useIsFirstRender} from '../../../util/componentUtil';
import {onInvoicePreview} from '../../../util/events';
import {
    ActivateHandle,
    InvoiceRequestsTab,
    InvoiceType,
    InvoiceUploadStats,
    InvoiceUploadStatus
} from '../../../util/interfaces';
import {setOpenInvoiceRequestCount} from '../../../util/invoiceRequestUtil';
import {setNavigationParam} from '../../../util/navigateUtil';
import {downloadInvoice, uploadInvoice, uploadMultipleInvoices} from '../../../util/requestSender';
import {createErrorMessage} from '../../../util/utils';
import {AppContext} from '../../misc/appContext';
import InvoiceDataController from './invoiceData/invoiceDataController';
import InvoiceRequests from './invoiceRequests';
import {showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

interface Props {
    reloadNumOpen: VoidFunction;
    stats: InvoiceUploadStats;
}

type RequestsLoadParams = {search?: string; page?: number; status?: InvoiceUploadStatus; limit?: number};

function tabFromParams(): InvoiceRequestsTab {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tab') === 'instellingen') return InvoiceRequestsTab.SETTINGS;
    return InvoiceRequestsTab.REQUESTS;
}

export default function InvoiceRequestController(props: Props) {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {t} = useTranslation();
    const {reloadNumOpen, stats} = props;
    const [invoiceRequests, setInvoiceRequests] = useState<Array<BolInvoiceRequest>>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState<InvoiceUploadStatus>(InvoiceUploadStatus.OPEN);
    const [limit, setLimit] = useState(10);
    const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(new Set());
    const [allSelected, setAllSelected] = useState(false);
    const [tab, setTab] = useState<InvoiceRequestsTab>(tabFromParams());
    const navigate = useNavigate();
    const isFirstRender = useIsFirstRender();
    const invoiceDataRef = useRef<ActivateHandle>(null);

    function onChangeTab(tab: InvoiceRequestsTab) {
        setTab(tab);
    }

    function onPreviewInvoice(invoiceId: string) {
        return function () {
            if (!account) return;
            setLoading(true);
            downloadInvoice({
                invoice_id: invoiceId,
                account_id: account.id,
                noHistory: true,
                callback: (data: ResponseData, response: Response) => {
                    setLoading(false);
                    if (data.status === 'success')
                        // FILE RESPONSE
                        response.blob().then((blob: Blob) => {
                            onInvoicePreview(URL.createObjectURL(blob));
                        });
                    else if (!data.handled)
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t('invoice.request.downloadFailed', 'Het downloaden van de factuur is niet gelukt.')
                            ),
                            t('invoice.request.downloadFailedTitle', 'Downloaden mislukt')
                        );
                }
            });
        };
    }

    function onDownloadInvoice(invoiceId: string) {
        return function () {
            if (!account) return;
            setLoading(true);
            downloadInvoice({
                invoice_id: invoiceId,
                account_id: account.id,
                callback: (data: ResponseData, response: Response) => {
                    setLoading(false);
                    if (data.status === 'success') {
                        // FILE RESPONSE
                        response.blob().then(blob => {
                            const header = response.headers.get('Content-Disposition');
                            let fileName = 'factuur.pdf';
                            if (header)
                                fileName = header.substring(
                                    header.indexOf('filename=') + 'filename='.length,
                                    header.length
                                );
                            downloadFile(blob, fileName);
                        });
                    } else
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t('invoice.request.downloadFailed', 'Het downloaden van de factuur is niet gelukt.')
                            ),
                            t('invoice.request.downloadFailedTitle', 'Downloaden mislukt')
                        );
                }
            });
        };
    }

    function onEditInvoice(invoiceId: string) {
        return function () {
            setNavigationParam(invoiceId);
            navigate('/factuur-wijzigen');
        };
    }

    function onUploadInvoice(invoiceRequest: BolInvoiceRequest) {
        return function (evt?: React.MouseEvent) {
            if (evt) stopPropagation(evt);
            if (!account) return;
            setLoading(true);
            uploadInvoice({
                data: {
                    invoice_id: invoiceRequest.invoiceId,
                    shipment_id: nullify(invoiceRequest.shipmentId),
                    account_id: account.id
                },
                callback: (data: ResponseData) => {
                    setLoading(false);
                    if (data.success) {
                        showSnackbar(t('invoice.request.uploadingSuccess', 'De factuur wordt geüpload naar Bol'));
                        loadInvoiceRequests();
                        reloadNumOpen();
                    } else
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t('invoice.request.uploadFailed', 'Het uploaden van de factuur is niet gelukt.')
                            ),
                            t('invoice.request.uploadFailedTitle', 'Uploaden mislukt')
                        );
                }
            });
        };
    }

    function onUploadMultipleInvoices(confirmed: boolean = false) {
        return function () {
            if (!account) return;
            if (!selectedInvoices && !confirmed)
                return showDialog(
                    t(
                        'invoice.request.uploadAllConfirm',
                        'Hiermee worden de facturen voor alle openstaande factuuraanvragen geüpload. Weet je zeker dat je door wilt gaan?'
                    ),
                    t('invoice.request.confirmTitle', 'Bevestigen'),
                    [
                        {label: t('common.no', 'Nee'), primary: false},
                        {
                            label: t('common.yes', 'Ja'),
                            onClick: onUploadMultipleInvoices(true)
                        }
                    ]
                );
            setLoading(true);
            uploadMultipleInvoices({
                data: {
                    account_id: account.id,
                    upload_all_open: !selectedInvoices.size,
                    upload_invoice_ids: nullify(Array.from(selectedInvoices))
                },
                callback: (data: ResponseData) => {
                    if (data.success) {
                        showSnackbar(t('invoice.request.bulkUploading', 'De facturen worden naar Bol geüpload'));
                        loadInvoiceRequests();
                        reloadNumOpen();
                    } else {
                        setLoading(false);
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t(
                                        'invoice.request.bulkUploadFailed',
                                        'Het uploaden van één of meer facturen is niet gelukt.'
                                    )
                            ),
                            t('invoice.request.uploadFailedTitle', 'Uploaden mislukt')
                        );
                    }
                }
            });
        };
    }

    function onSelectRequest(invoiceId: string) {
        return function (checked: boolean, evt: React.ChangeEvent) {
            stopPropagation(evt);
            const selected = new Set(selectedInvoices);
            if (selected.has(invoiceId)) selected.delete(invoiceId);
            else selected.add(invoiceId);
            setSelectedInvoices(selected);
            setAllSelected(false);
        };
    }

    function onSelectAll() {
        setAllSelected(!allSelected);
        setSelectedInvoices(new Set());
    }

    function onChangePage(_page: number) {
        return function () {
            if (_page === page) return;
            setPage(_page);
            loadInvoiceRequests({page: _page});
        };
    }

    function onChangeSearch(value: string) {
        setSearch(value);
    }

    function onEnterSearch() {
        loadInvoiceRequests();
    }

    function onChangeStatus(_status: InvoiceUploadStatus) {
        return function () {
            if (_status === status) return;
            setStatus(_status);
            loadInvoiceRequests({status: _status, page: 0});
        };
    }

    function onChangeLimit(_limit: number) {
        if (_limit === limit) return;
        setLimit(_limit);
        loadInvoiceRequests({limit: _limit});
    }

    function onConfirmArchive(invoiceRequest: BolInvoiceRequest) {
        return function () {
            showDialog(
                t(
                    'invoice.request.archiveConfirm',
                    'Weet je zeker dat je deze aanvraag wilt archiveren? De aanvraag zal niet meer getoond worden. Deze actie kan niet ongedaan gemaakt worden.'
                ),
                t('invoice.request.archiveTitle', 'Aanvraag archiveren'),
                [
                    t('common.no', 'Nee'),
                    {
                        label: t('common.yes', 'Ja'),
                        onClick: onArchive(invoiceRequest),
                        primary: false
                    }
                ]
            );
        };
    }

    function onArchive(invoiceRequest: BolInvoiceRequest) {
        return function () {
            setLoading(true);
            invoiceRequest.archive((invoiceRequest: BolInvoiceRequest, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    removeInvoiceRequest(invoiceRequest);
                    setOpenInvoiceRequestCount(-1);
                    showSnackbar(t('invoice.request.archivedSuccess', 'De aanvraag is gearchiveerd'));
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t('invoice.request.archiveFailed', 'Het archiveren van de aanvraag is niet gelukt.')
                        ),
                        t('invoice.request.archiveFailedTitle', 'Archiveren mislukt')
                    );
            });
        };
    }

    function onFetchOrder(invoiceRequest: BolInvoiceRequest) {
        return function (evt: React.MouseEvent) {
            evt.stopPropagation();
            if (!account) return;
            setLoading(true);
            Invoice.getAll(
                account.id,
                {order_id: invoiceRequest.orderId, invoice_type: InvoiceType.DEBET},
                -1,
                -1,
                (invoices: Array<Invoice>, success: boolean, data: ResponseData) => {
                    if (success) {
                        if (!invoices.length) {
                            setLoading(false);
                            return showDialog(
                                t(
                                    'invoice.request.orderNotFoundBody',
                                    'We konden geen order vinden voor deze aanvraag. Dit betekent zeer waarschijnlijk dat het gaat om een order die ouder is dan twee jaar. Doordat bol geen gegevens geeft voor orders dan twee jaar, kunnen wij deze aanvraag niet voor je verwerken.'
                                ),
                                t('invoice.request.orderNotFoundTitle', 'Order niet gevonden')
                            );
                        }
                        const invoice = invoices[0];
                        if (invoice.invoiceNum.length)
                            // Pre-existing invoice
                            return onSetInvoice(invoiceRequest, invoice);
                        return onNewInvoice(invoiceRequest, invoice);
                    } else {
                        setLoading(false);
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t(
                                        'invoice.request.fetchOrderFailed',
                                        'Er is iets misgegaan bij het ophalen van de order.'
                                    )
                            ),
                            t('invoice.request.fetchOrderFailedTitle', 'Order ophalen mislukt')
                        );
                    }
                }
            );
        };
    }

    function onSetInvoice(invoiceRequest: BolInvoiceRequest, invoice: Invoice, upload: boolean = false) {
        setLoading(true);
        invoiceRequest.setInvoice(
            invoice,
            (invoiceRequest: BolInvoiceRequest, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    if (upload) onUploadInvoice(invoiceRequest)();
                    else replaceInvoiceRequest(invoiceRequest);
                } else
                    showDialog(
                        createErrorMessage(data.message ?? t('invoice.request.somethingWrong', 'Er is iets misgegaan.'))
                    );
            }
        );
    }

    function onNewInvoice(invoiceRequest: BolInvoiceRequest, invoice: Invoice) {
        setLoading(false);
        invoiceDataRef.current?.activate(invoiceRequest, invoice);
    }

    function replaceInvoiceRequest(invoiceRequest: BolInvoiceRequest) {
        const requests = invoiceRequests.slice();
        for (let ix = 0; ix < requests.length; ix++) {
            if (requests[ix].id === invoiceRequest.id) {
                requests.splice(ix, 1, invoiceRequest);
                setInvoiceRequests(requests);
                return;
            }
        }
    }

    function removeInvoiceRequest(invoiceRequest: BolInvoiceRequest) {
        const requests = invoiceRequests.slice();
        for (const req of requests) {
            if (req.orderId == invoiceRequest.orderId && req.shipmentId == invoiceRequest.shipmentId) {
                requests.splice(requests.indexOf(req), 1);
                setInvoiceRequests(requests);
                break;
            }
        }
    }

    function loadInvoiceRequests(params?: RequestsLoadParams) {
        if (!account) return;
        setURLParams(params);
        const _search = params?.search ?? search;
        const _page = params?.page ?? page;
        const _status = params?.status === undefined ? status : params.status;
        const _limit = params?.limit ?? limit;
        setLoading(true);
        BolInvoiceRequest.getAll(
            {
                page: _page,
                search: nullify(_search),
                status: _status,
                account_id: account.id,
                limit: _limit,
                country: null
            },
            (requests: Array<BolInvoiceRequest>, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    setInvoiceRequests(requests);
                    setTotal(data.total);
                    if (_status === InvoiceUploadStatus.OPEN) setOpenInvoiceRequestCount(data.total);
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t('invoice.request.loadFailed', 'Het laden van de factuuraanvragen is niet gelukt.')
                        ),
                        t('invoice.request.loadFailedTitle', 'Laden mislukt')
                    );
            }
        );
    }

    function setURLParams(params?: RequestsLoadParams) {
        const _search = nullify(params?.search ?? search);
        const _page = params?.page ?? page;
        const _status = params?.status === undefined ? status : params.status;
        const _limit = params?.limit ?? limit;
        const {origin, pathname} = window.location;
        let url = `${origin}${pathname}?page=${_page}&limit=${_limit}`;
        if (_search) url += `&search=${_search}`;
        if (_status) url += `&status=${_status}`;
        history.replaceState({}, '', url);
    }

    function getURLParams() {
        const params = new URLSearchParams(location.search);
        const _search = params.get('search') ?? search;
        const _page = Number(params.get('page') ?? 0);
        const _status = (params.get('status') ?? status) as InvoiceUploadStatus;
        const _limit = Number(params.get('limit') ?? limit);
        const _tab = params.get('tab');
        setSearch(_search);
        setPage(_page);
        setStatus(_status);
        setLimit(_limit);
        loadInvoiceRequests({search: _search, page: _page, status: _status, limit: _limit});
        if (_tab === 'settings') {
            setTab(InvoiceRequestsTab.SETTINGS);
            saveAccountSetupProgress();
        }
    }

    function saveAccountSetupProgress() {
        const {account} = context.accountContext;
        if (!account || account.setupProgress.upload) return;
        account.setupProgress.upload = true;
        account.update(undefined, undefined, undefined, (account: Account, success: boolean) => {
            if (success) context.onChangeContext({account});
        });
    }

    useEffect(() => {
        getURLParams();
    }, []);

    useEffect(() => {
        if (!isFirstRender) loadInvoiceRequests();
    }, [context.accountContext.account]);

    return (
        <>
            <InvoiceRequests
                onChangeSearch={onChangeSearch}
                onChangePage={onChangePage}
                onChangeStatus={onChangeStatus}
                onChangeLimit={onChangeLimit}
                onSelectRequest={onSelectRequest}
                onEnterSearch={onEnterSearch}
                onEditInvoice={onEditInvoice}
                onPreviewInvoice={onPreviewInvoice}
                onChangeTab={onChangeTab}
                onUploadInvoice={onUploadInvoice}
                onDownloadInvoice={onDownloadInvoice}
                onUploadMultipleInvoices={onUploadMultipleInvoices()}
                onSelectAll={onSelectAll}
                onArchive={onConfirmArchive}
                onFetchOrder={onFetchOrder}
                invoiceRequests={invoiceRequests}
                total={total}
                loading={loading}
                search={search}
                page={page}
                status={status}
                limit={limit}
                selectedInvoices={selectedInvoices}
                stats={stats}
                allSelected={allSelected}
                tab={tab}
            />
            <InvoiceDataController ref={invoiceDataRef} onSuccess={onSetInvoice} />
        </>
    );
}
